import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';

const APP_CONTROL_TOP_50 = [
  'all.auth.signIn',
  'all.auth.signUp',
  'all.auth.forgotPassword',
  'all.auth.onboarding',
  'all.auth.consent',
  'all.auth#biometricUnlock',
  'all.app#locationCapture',
  'all.app#rootBlock',
  'all.app#screenshotBlock',
  'all.app#deviceTelemetry',
  'member.home',
  'member.home.notifications',
  'member.home.chat',
  'member.team',
  'member.team.addMember',
  'member.wallet',
  'member.wallet.transfer',
  'member.wallet.convert',
  'member.wallet.withdraw',
  'member.wallet.history',
  'member.shop',
  'member.shop.search',
  'member.shop.product',
  'member.shop.cart',
  'member.shop.checkout',
  'member.shop.orders',
  'member.promotion.status',
  'member.promotion.ladder',
  'member.sales.mine',
  'member.profile.edit',
  'member.profile.bank',
  'member.profile.kyc',
  'member.profile.password',
  'all.profile.settings',
  'all.profile.security',
  'agent.home',
  'agent.orders',
  'agent.withdrawals',
  'agent.products',
  'manager.users',
  'manager.search',
  'manager.report',
  'admin.dashboard',
  'admin.users',
  'admin.money',
  'admin.ops',
  'admin.backup',
  'admin.config',
  'admin.appControl',
  'admin.push',
];

const CONTROL_GROUPS = [
  { key: 'all', label: 'Auth & Platform', keys: ['all.auth.signIn', 'all.auth.signUp', 'all.auth.forgotPassword', 'all.auth.onboarding', 'all.auth.consent', 'all.auth#biometricUnlock', 'all.app#locationCapture', 'all.app#rootBlock', 'all.app#screenshotBlock', 'all.app#deviceTelemetry', 'all.profile.settings', 'all.profile.security'] },
  { key: 'member', label: 'Member', keys: ['member.home', 'member.home.notifications', 'member.home.chat', 'member.team', 'member.team.addMember', 'member.wallet', 'member.wallet.transfer', 'member.wallet.convert', 'member.wallet.withdraw', 'member.wallet.history', 'member.shop', 'member.shop.search', 'member.shop.product', 'member.shop.cart', 'member.shop.checkout', 'member.shop.orders', 'member.promotion.status', 'member.promotion.ladder', 'member.sales.mine', 'member.profile.edit', 'member.profile.bank', 'member.profile.kyc', 'member.profile.password'] },
  { key: 'agent', label: 'Agent', keys: ['agent.home', 'agent.orders', 'agent.withdrawals', 'agent.products'] },
  { key: 'manager', label: 'Manager', keys: ['manager.users', 'manager.search', 'manager.report'] },
  { key: 'admin', label: 'Admin', keys: ['admin.dashboard', 'admin.users', 'admin.money', 'admin.ops', 'admin.backup', 'admin.config', 'admin.appControl', 'admin.push'] },
];

// Actions whose run goes through useSecureAction in the mobile app — the only
// place requireBiometric is honoured. Showing the switch anywhere else would
// promise a fingerprint check the app never asks for.
const SECURE_ACTION_KEYS = new Set([
  'member.wallet.transfer#submit',
  'member.wallet.convert#submit',
  'member.wallet.withdraw#submit',
  'member.wallet.associationTransfer#submit',
  'member.team.addMember#submit',
  'member.requestMoney#submit',
  'member.merchant.moneyRequests#approve',
  'member.shop.checkout#placeOrder',
  'admin.global#distributeNow',
]);

const ROLE_ORDER = ['all', 'member', 'agent', 'manager', 'admin'];
const ROLE_LABELS = { all: 'Everyone (auth & platform)', member: 'Member', agent: 'Agent / staff', manager: 'Manager', admin: 'Admin' };
const KIND_COLOURS = { screen: '#2563eb', section: '#7c3aed', action: '#d97706' };

function inferKind(key) {
  const k = String(key || '');
  if (k.includes('#')) return 'action';
  if (k.includes(':')) return 'section';
  return 'screen';
}

function fallbackControlDef(key) {
  const [role = 'all'] = String(key || '').split('.');
  const area = String(key || '').split(/[.#:]/).slice(0, 2).join('.');
  return { key, role, area, label: key, kind: inferKind(key), description: '', locked: false, defaultEnabled: true };
}

/**
 * What the phone assumes for a key the server has no row for — mirrors
 * defaultControlMap() in the app's registry.ts, so creating a row here (say,
 * to add only a message) does not silently change how the gate behaves.
 */
function baseControlState(def) {
  const on = def?.defaultEnabled !== false;
  return {
    enabled: on,
    hidden: !on && def?.kind !== 'action',
    message: '',
    requireOtp: false,
    requireBiometric: false,
    disabledFrom: null,
    disabledUntil: null,
    minDesignation: null,
  };
}

const pad2 = (n) => String(n).padStart(2, '0');

/** ISO (UTC) -> value for a datetime-local box, in the admin's own timezone. */
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** datetime-local value (local time, no zone) -> ISO (UTC). */
function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const greyBtn = { background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' };
const smallBtn = { ...greyBtn, padding: '6px 10px', fontSize: 12 };
const textInput = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13 };
const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };

function Badge({ children, colour = '#6b7280' }) {
  return (
    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: colour, border: `1px solid ${colour}55`, background: `${colour}12`, borderRadius: 999, padding: '1px 7px', marginRight: 4 }}>
      {children}
    </span>
  );
}

function Toggle({ on, onClick, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ width: 44, height: 24, borderRadius: 999, border: 'none', position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', padding: 2, opacity: disabled ? 0.55 : 1, background: on ? '#22c55e' : '#cbd5e1', flexShrink: 0 }}>
      <span style={{ display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'transform 0.15s', transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  );
}

function GateRow({ def, c, dirty, expanded, onToggleExpand, onPatch }) {
  const locked = Boolean(def.locked);
  const biometricApplies = def.kind === 'action' && SECURE_ACTION_KEYS.has(def.key);
  const hasWindow = Boolean(c.disabledFrom || c.disabledUntil);
  return (
    <div style={{ border: `1px solid ${dirty ? '#93c5fd' : '#eef0f3'}`, borderRadius: 10, padding: 12, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>{def.label || def.key}</strong>
            <Badge colour={KIND_COLOURS[def.kind] || '#6b7280'}>{def.kind || 'screen'}</Badge>
            {locked ? <Badge colour="#16a34a">locked · always on</Badge> : null}
            {!locked && c.hidden ? <Badge>hidden when off</Badge> : null}
            {!locked && hasWindow ? <Badge colour="#dc2626">scheduled</Badge> : null}
            {!locked && c.minDesignation != null ? <Badge colour="#0891b2">level {c.minDesignation}+</Badge> : null}
            {!locked && biometricApplies && c.requireBiometric ? <Badge colour="#7c3aed">biometric</Badge> : null}
            {!locked && c.message ? <Badge>message</Badge> : null}
            {dirty ? <Badge colour="#2563eb">unsaved</Badge> : null}
          </div>
          <span style={{ color: '#6b7280', fontSize: 12, display: 'block', marginTop: 2, wordBreak: 'break-all', fontFamily: 'monospace' }}>{def.key}</span>
          {def.description ? <span style={{ color: '#4b5563', fontSize: 12, display: 'block', marginTop: 2 }}>{def.description}</span> : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!locked ? (
            <button type="button" onClick={onToggleExpand} style={smallBtn}>{expanded ? 'Close' : 'Edit'}</button>
          ) : null}
          <Toggle on={locked ? true : c.enabled} disabled={locked} onClick={() => onPatch({ enabled: !c.enabled })} />
        </div>
      </div>

      {expanded && !locked ? (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e5e7eb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          <div>
            <span style={fieldLabel}>Hide when off</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Toggle on={Boolean(c.hidden)} onClick={() => onPatch({ hidden: !c.hidden })} />
              <span style={{ fontSize: 12, color: '#6b7280' }}>{def.kind === 'action' ? 'Actions are not drawn differently' : 'Off = hidden, not greyed out'}</span>
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={fieldLabel}>Message shown when off (blank = default)</span>
            <input style={textInput} value={c.message || ''} maxLength={300} onChange={(e) => onPatch({ message: e.target.value })} placeholder="This feature is temporarily unavailable. Please try again later." />
          </div>
          <div>
            <span style={fieldLabel}>Off from (your local time)</span>
            <input style={textInput} type="datetime-local" value={isoToLocalInput(c.disabledFrom)} onChange={(e) => onPatch({ disabledFrom: localInputToIso(e.target.value) })} />
          </div>
          <div>
            <span style={fieldLabel}>Off until (your local time)</span>
            <input style={textInput} type="datetime-local" value={isoToLocalInput(c.disabledUntil)} onChange={(e) => onPatch({ disabledUntil: localInputToIso(e.target.value) })} />
          </div>
          <div>
            <span style={fieldLabel}>Minimum designation level</span>
            <input
              style={textInput}
              type="number"
              min={0}
              value={c.minDesignation == null ? '' : c.minDesignation}
              onChange={(e) => onPatch({ minDesignation: e.target.value === '' ? null : Number(e.target.value) })}
              placeholder="Blank = everyone"
            />
          </div>
          {biometricApplies ? (
            <div>
              <span style={fieldLabel}>Require fingerprint / face</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Toggle on={Boolean(c.requireBiometric)} onClick={() => onPatch({ requireBiometric: !c.requireBiometric })} />
                <span style={{ fontSize: 12, color: '#6b7280' }}>Asked before the request is sent</span>
              </div>
            </div>
          ) : null}
          <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span>
              A schedule switches the gate off between the two times, even while the switch above is on. Only "until" = off now until then; only "from" = off from then on.
            </span>
            {hasWindow ? (
              <button type="button" style={smallBtn} onClick={() => onPatch({ disabledFrom: null, disabledUntil: null })}>Clear schedule</button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AppControlScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [appConfig, setAppConfig] = useState(null);
  const [controlRegistry, setControlRegistry] = useState([]);
  const [registryVersion, setRegistryVersion] = useState(0);
  const [controlState, setControlState] = useState({});
  // Keys this admin changed since the last load/save. Only these are written,
  // on top of a fresh copy of the server map, so a stale tab cannot undo a
  // change someone else made in the meantime (e.g. from the phone).
  const [dirtyKeys, setDirtyKeys] = useState({});
  const [controlQuery, setControlQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [logoutAllBefore, setLogoutAllBefore] = useState('');

  const [gateQuery, setGateQuery] = useState('');
  const [gateRole, setGateRole] = useState('');
  const [gateKind, setGateKind] = useState('');
  const [onlyOff, setOnlyOff] = useState(false);
  const [expanded, setExpanded] = useState({});

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${userInfo?.token}` }), [userInfo]);
  const dirtyCount = Object.keys(dirtyKeys).length;

  const resolveControl = useCallback((def) => {
    const current = { ...baseControlState(def), ...(controlState?.[def.key] || {}) };
    return {
      ...current,
      enabled: def.locked ? true : current.enabled !== false,
      hidden: Boolean(current.hidden),
      message: String(current.message || ''),
    };
  }, [controlState]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [cfgRes, regRes] = await Promise.all([
        axios.get(api + '/api/app/config', { headers: authHeaders }),
        axios.get(api + '/api/app/controls/registry', { headers: authHeaders }),
      ]);
      setAppConfig(cfgRes.data || null);
      setLogoutAllBefore(isoToLocalInput(cfgRes.data?.app?.logoutAllBefore));
      setControlRegistry(Array.isArray(regRes.data?.definitions) ? regRes.data.definitions : []);
      setRegistryVersion(Number(regRes.data?.registryVersion) || 0);
      setControlState(regRes.data?.controls && typeof regRes.data.controls === 'object' ? regRes.data.controls : {});
      setDirtyKeys({});
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  }, [authHeaders]);

  const reload = () => {
    if (dirtyCount && !window.confirm(`Discard ${dirtyCount} unsaved control change${dirtyCount === 1 ? '' : 's'} and reload?`)) return;
    void load();
  };

  const runtime = appConfig?.app || {};

  const defsByKey = useMemo(() => {
    const map = new Map();
    for (const key of APP_CONTROL_TOP_50) map.set(key, fallbackControlDef(key));
    for (const def of controlRegistry) {
      if (def?.key) map.set(def.key, { ...fallbackControlDef(def.key), ...def });
    }
    return map;
  }, [controlRegistry]);

  const top50Defs = useMemo(() => APP_CONTROL_TOP_50.map((key) => defsByKey.get(key) || fallbackControlDef(key)), [defsByKey]);

  // Every gate the app has published; the Top 50 stand in until a phone has
  // registered its vocabulary.
  const allDefs = useMemo(() => {
    if (!controlRegistry.length) return top50Defs;
    return controlRegistry.filter((d) => d?.key).map((d) => ({ ...fallbackControlDef(d.key), ...d }));
  }, [controlRegistry, top50Defs]);

  const updateAppConfig = (path, value) => {
    setAppConfig((cur) => {
      const next = structuredClone(cur || {});
      let node = next;
      const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i += 1) {
        node[parts[i]] = node[parts[i]] ?? {};
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const saveRuntime = async () => {
    setBusy(true);
    try {
      const payload = {
        app: {
          enabled: runtime.enabled !== false,
          loginEnabled: runtime.loginEnabled !== false,
          loginRoles: {
            staff: runtime.loginRoles?.staff !== false,
            member: runtime.loginRoles?.member !== false,
          },
          loginBlockedMessage: runtime.loginBlockedMessage || '',
          logoutAllBefore: localInputToIso(logoutAllBefore),
        },
      };
      const { data } = await axios.put(api + '/api/app/config', payload, { headers: authHeaders });
      if (data?.config) {
        setAppConfig(data.config);
        setLogoutAllBefore(isoToLocalInput(data.config.app?.logoutAllBefore));
      }
      toast.success(data?.message || 'Saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  /** Apply a patch to some gates, keeping every other field they already have. Locked gates are skipped. */
  const patchControls = useCallback((defs, patch) => {
    const editable = defs.filter((def) => def && !def.locked);
    if (!editable.length) return;
    setControlState((cur) => {
      const next = { ...(cur || {}) };
      for (const def of editable) {
        next[def.key] = { ...baseControlState(def), ...(cur?.[def.key] || {}), ...patch };
      }
      return next;
    });
    setDirtyKeys((cur) => {
      const next = { ...cur };
      for (const def of editable) next[def.key] = true;
      return next;
    });
  }, []);

  const setTop50Enabled = (enabled) => patchControls(top50Defs, { enabled });

  const saveControls = async () => {
    const keys = Object.keys(dirtyKeys);
    if (!keys.length) {
      toast.info('No control changes to save.');
      return;
    }
    setBusy(true);
    try {
      // Start from what the server has NOW, and lay only this admin's edits on top.
      const { data: latest } = await axios.get(api + '/api/app/controls/registry', { headers: authHeaders });
      const merged = { ...(latest?.controls && typeof latest.controls === 'object' ? latest.controls : {}) };
      for (const key of keys) {
        if (controlState[key]) merged[key] = controlState[key];
      }
      const version = Number(latest?.registryVersion ?? registryVersion) || 0;
      const { data } = await axios.put(api + '/api/app/controls', { controls: merged, registryVersion: version }, { headers: authHeaders });
      setControlState(data?.controls && typeof data.controls === 'object' ? data.controls : merged);
      if (Array.isArray(latest?.definitions)) setControlRegistry(latest.definitions);
      setRegistryVersion(version);
      setDirtyKeys({});
      toast.success(data?.message || 'Saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const discardControls = async () => {
    if (!window.confirm(`Discard ${dirtyCount} unsaved control change${dirtyCount === 1 ? '' : 's'}?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.get(api + '/api/app/controls/registry', { headers: authHeaders });
      setControlState(data?.controls && typeof data.controls === 'object' ? data.controls : {});
      if (Array.isArray(data?.definitions)) setControlRegistry(data.definitions);
      setDirtyKeys({});
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  const groupedTop50 = useMemo(() => {
    const byKey = new Map(top50Defs.map((def) => [def.key, def]));
    return CONTROL_GROUPS.map((group) => ({
      ...group,
      defs: group.keys.map((key) => byKey.get(key)).filter(Boolean),
    }));
  }, [top50Defs]);

  const saveGroup = (defs, enabled) => patchControls(defs, { enabled });

  const roleOptions = useMemo(() => {
    const roles = Array.from(new Set(allDefs.map((d) => d.role || 'all')));
    return roles.sort((a, b) => {
      const ia = ROLE_ORDER.indexOf(a);
      const ib = ROLE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
    });
  }, [allDefs]);

  const groupedGates = useMemo(() => {
    const q = gateQuery.trim().toLowerCase();
    const byRole = new Map();
    for (const role of roleOptions) byRole.set(role, new Map());
    for (const def of allDefs) {
      const role = def.role || 'all';
      if (gateRole && role !== gateRole) continue;
      if (gateKind && (def.kind || 'screen') !== gateKind) continue;
      if (onlyOff && (def.locked || resolveControl(def).enabled)) continue;
      if (q && !`${def.key} ${def.label || ''} ${def.area || ''} ${def.role || ''} ${def.description || ''}`.toLowerCase().includes(q)) continue;
      const areas = byRole.get(role) || new Map();
      byRole.set(role, areas);
      const area = def.area || 'Other';
      if (!areas.has(area)) areas.set(area, []);
      areas.get(area).push(def);
    }
    return Array.from(byRole.entries())
      .map(([role, areas]) => ({ role, areas: Array.from(areas.entries()).map(([area, defs]) => ({ area, defs })) }))
      .filter((g) => g.areas.length);
  }, [allDefs, roleOptions, gateQuery, gateRole, gateKind, onlyOff, resolveControl]);

  const shownGateCount = groupedGates.reduce((n, g) => n + g.areas.reduce((m, a) => m + a.defs.length, 0), 0);

  useEffect(() => {
    void load();
  }, [load]);

  // Warn before closing the tab with control edits still unsaved.
  useEffect(() => {
    if (!dirtyCount) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtyCount]);

  const saveBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: dirtyCount ? '#b45309' : '#6b7280' }}>
        {dirtyCount ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}` : 'No unsaved changes'}
      </span>
      {dirtyCount ? <button type="button" onClick={discardControls} disabled={busy} style={greyBtn}>Discard</button> : null}
      <button type="button" onClick={saveControls} disabled={busy || !dirtyCount} style={{ background: dirtyCount ? '#2563eb' : '#93c5fd', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontWeight: 700, cursor: dirtyCount ? 'pointer' : 'default' }}>{busy ? 'Saving…' : 'Save controls'}</button>
    </div>
  );
  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 16px 80px' }}>
      <Helmet><title>App Control</title></Helmet>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>App Control Centre</h3>
        <p style={{ margin: '4px 0 0', color: '#6b7280' }}>Every gate the mobile app publishes can be switched here. Changes reach phones on their next config load.</p>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <h5 style={{ margin: '0 0 10px' }}>Mobile App Runtime</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          <div style={{ border: '1px solid #eef0f3', borderRadius: 10, padding: 12 }}>
            <strong>App globally enabled</strong>
            <p style={{ margin: '4px 0 10px', color: '#6b7280' }}>Off blocks non-admin devices with maintenance mode.</p>
            <Toggle on={runtime.enabled !== false} onClick={() => updateAppConfig('app.enabled', !(runtime.enabled !== false))} />
          </div>
          <div style={{ border: '1px solid #eef0f3', borderRadius: 10, padding: 12 }}>
            <strong>User login enabled</strong>
            <p style={{ margin: '4px 0 10px', color: '#6b7280' }}>Off blocks sign-in for non-admin users in the mobile app.</p>
            <Toggle on={runtime.loginEnabled !== false} onClick={() => updateAppConfig('app.loginEnabled', !(runtime.loginEnabled !== false))} />
          </div>
        </div>
        <div style={{ marginTop: 12, border: '1px solid #eef0f3', borderRadius: 10, padding: 12 }}>
          <strong>Who can use the mobile app</strong>
          <p style={{ margin: '4px 0 10px', color: '#6b7280' }}>
            Turning a type off refuses its sign-in and signs out anyone of that type already in the app (within about 15 seconds). The website is not affected. Admins can always sign in.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {[
              { key: 'admin', label: 'Admins', hint: 'Always allowed' },
              { key: 'staff', label: 'Staff', hint: 'Agents, user managers, product managers' },
              { key: 'member', label: 'Members', hint: 'Everyone else, merchants included. Also closes app sign-up.' },
            ].map((t) => {
              const on = t.key === 'admin' ? true : runtime.loginRoles?.[t.key] !== false;
              return (
                <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid #eef0f3', borderRadius: 10, padding: 10, background: '#fafafa' }}>
                  <div>
                    <strong style={{ fontSize: 13, display: 'block' }}>{t.label}</strong>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>{t.hint}</span>
                  </div>
                  {t.key === 'admin' ? (
                    <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 12 }}>ON</span>
                  ) : (
                    <Toggle on={on} onClick={() => updateAppConfig(`app.loginRoles.${t.key}`, !on)} />
                  )}
                </div>
              );
            })}
          </div>
          <input
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14, marginTop: 10 }}
            placeholder="Message shown to a refused account (blank = default)"
            maxLength={300}
            value={runtime.loginBlockedMessage || ''}
            onChange={(e) => updateAppConfig('app.loginBlockedMessage', e.target.value)}
          />
        </div>
        <div style={{ marginTop: 12, border: '1px solid #eef0f3', borderRadius: 10, padding: 12 }}>
          <strong>Force logout all users</strong>
          <p style={{ margin: '4px 0 10px', color: '#6b7280' }}>Users with sessions older than this timestamp will be signed out when the app refreshes config/health.</p>
          <input
            style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14 }}
            type="datetime-local"
            value={logoutAllBefore}
            onChange={(e) => setLogoutAllBefore(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={saveRuntime} disabled={busy} style={{ background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' }}>{busy ? 'Saving…' : 'Save runtime toggles'}</button>
          <button onClick={reload} disabled={busy} style={{ background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' }}>{busy ? 'Loading…' : 'Reload'}</button>
        </div>
      </div>


      <fieldset disabled={busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h5 style={{ margin: 0 }}>Top 50 Quick Switches</h5>
            <p style={{ margin: '4px 0 0', color: '#6b7280' }}>The most-used gates. Every gate, with schedules and messages, is in "All app gates" below.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setTop50Enabled(true)} style={greyBtn}>Enable all 50</button>
            <button type="button" onClick={() => setTop50Enabled(false)} style={greyBtn}>Disable all 50</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <input style={{ flex: '1 1 260px', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14 }} placeholder="Search controls" value={controlQuery} onChange={(e) => setControlQuery(e.target.value)} />
          {saveBar}
        </div>
        <div style={{ display: 'grid', gap: 14 }}>
          {groupedTop50.map((group) => {
            const defs = controlQuery.trim() ? group.defs.filter((def) => `${def.key} ${def.label || ''} ${def.area || ''} ${def.role || ''}`.toLowerCase().includes(controlQuery.trim().toLowerCase())) : group.defs;
            if (!defs.length) return null;
            return (
              <div key={group.key} style={{ border: '1px solid #eef0f3', borderRadius: 12, padding: 14, background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ display: 'block', fontSize: 14 }}>{group.label}</strong>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>{defs.length} implemented gate{defs.length === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => saveGroup(defs, true)} style={{ ...greyBtn, padding: '8px 12px' }}>Enable group</button>
                    <button type="button" onClick={() => saveGroup(defs, false)} style={{ ...greyBtn, padding: '8px 12px' }}>Disable group</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 10 }}>
                  {defs.map((def) => {
                    const c = resolveControl(def);
                    return (
                      <div key={def.key} style={{ border: `1px solid ${dirtyKeys[def.key] ? '#93c5fd' : '#eef0f3'}`, borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#fff' }}>
                        <div style={{ minWidth: 0 }}>
                          <strong style={{ fontSize: 13, display: 'block' }}>{def.label || def.key}</strong>
                          <span style={{ color: '#6b7280', fontSize: 12, display: 'block', marginTop: 2, wordBreak: 'break-all' }}>{def.key}</span>
                          {def.locked ? <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 700 }}>Locked · always on</span> : null}
                        </div>
                        <Toggle on={c.enabled} disabled={Boolean(def.locked)} onClick={() => patchControls([def], { enabled: !c.enabled })} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h5 style={{ margin: 0 }}>All app gates</h5>
            <p style={{ margin: '4px 0 0', color: '#6b7280' }}>
              {controlRegistry.length
                ? `${allDefs.length} gates published by the app (registry v${registryVersion}). Showing ${shownGateCount}.`
                : 'No phone has published its gate list yet — showing the Top 50 only. Open the app once (signed in) to fill this.'}
            </p>
          </div>
          {saveBar}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, margin: '12px 0' }}>
          <input style={{ ...textInput, gridColumn: 'span 2', fontSize: 14, padding: '9px 11px' }} placeholder="Search key, label, area, description" value={gateQuery} onChange={(e) => setGateQuery(e.target.value)} />
          <select style={textInput} value={gateRole} onChange={(e) => setGateRole(e.target.value)}>
            <option value="">All roles</option>
            {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
          </select>
          <select style={textInput} value={gateKind} onChange={(e) => setGateKind(e.target.value)}>
            <option value="">All kinds</option>
            <option value="screen">Screens</option>
            <option value="section">Sections</option>
            <option value="action">Actions</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyOff} onChange={(e) => setOnlyOff(e.target.checked)} />
            Only switched off
          </label>
        </div>

        {!groupedGates.length ? (
          <p style={{ color: '#6b7280', margin: '12px 0 0' }}>No gates match these filters.</p>
        ) : null}

        <div style={{ display: 'grid', gap: 16 }}>
          {groupedGates.map((group) => (
            <div key={group.role}>
              <h6 style={{ margin: '0 0 8px', fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.4, color: '#374151' }}>{ROLE_LABELS[group.role] || group.role}</h6>
              <div style={{ display: 'grid', gap: 12 }}>
                {group.areas.map(({ area, defs }) => (
                  <div key={`${group.role}|${area}`} style={{ border: '1px solid #eef0f3', borderRadius: 12, padding: 14, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                      <div>
                        <strong style={{ display: 'block', fontSize: 14 }}>{area}</strong>
                        <span style={{ color: '#6b7280', fontSize: 12 }}>{defs.length} gate{defs.length === 1 ? '' : 's'}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => patchControls(defs, { enabled: true })} style={smallBtn}>Enable area</button>
                        <button type="button" onClick={() => patchControls(defs, { enabled: false })} style={smallBtn}>Disable area</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {defs.map((def) => (
                        <GateRow
                          key={def.key}
                          def={def}
                          c={resolveControl(def)}
                          dirty={Boolean(dirtyKeys[def.key])}
                          expanded={Boolean(expanded[def.key])}
                          onToggleExpand={() => setExpanded((cur) => ({ ...cur, [def.key]: !cur[def.key] }))}
                          onPatch={(patch) => patchControls([def], patch)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      </fieldset>

      {dirtyCount ? (
        <div style={{ position: 'sticky', bottom: 12, marginTop: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '10px 14px', display: 'flex', justifyContent: 'flex-end', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}>
          {saveBar}
        </div>
      ) : null}
    </div>
  );
}
